import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, Filter, User } from "lucide-react";

const ACTION_COLORS = {
  user_deleted: "bg-red-100 text-red-700",
  invoice_deleted: "bg-red-100 text-red-700",
  invoice_marked_paid: "bg-emerald-100 text-emerald-700",
  internal_notes_updated: "bg-blue-100 text-blue-700",
};

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/admin/audit", { params: { limit: 200 } }).then((r) => setItems(r.data));
  }, []);

  const filtered = items.filter((i) =>
    !filter ||
    (i.admin_name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (i.action || "").toLowerCase().includes(filter.toLowerCase()) ||
    (i.target_type || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="admin-audit">
      <p className="overline">Compliance</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Admin Audit Log</h1>
      <p className="text-sm text-[#475569] dark:text-slate-400 mt-1 mb-6">
        Every write-action by an admin (delete, mark paid, edit notes, etc.) is recorded here.
      </p>

      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-[#475569]" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by admin, action, or target type…"
          data-testid="audit-filter"
          className="flex-1 max-w-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm"
        />
        <span className="text-xs text-[#475569] dark:text-slate-400">{filtered.length} entries</span>
      </div>

      <div className="card-pb overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">When</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Admin</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Action</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Target</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.audit_id} className="border-b border-[#E2E8F0] dark:border-slate-700" data-testid={`audit-${a.audit_id}`}>
                <td className="px-5 py-3 text-xs text-[#475569] dark:text-slate-400 whitespace-nowrap">
                  {new Date(a.timestamp).toLocaleString()}
                </td>
                <td className="px-5 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-3 h-3 text-[#C9A356]" />
                    <span className="text-[#0A2540] dark:text-white font-medium">{a.admin_name}</span>
                  </div>
                  <div className="text-[10px] text-[#94A3B8]">{a.admin_email}</div>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                    ACTION_COLORS[a.action] || "bg-slate-100 text-slate-700"
                  }`}>{a.action.replace(/_/g, " ")}</span>
                </td>
                <td className="px-5 py-3 text-xs">
                  <div className="text-[#0A2540] dark:text-slate-200 capitalize">{a.target_type || "—"}</div>
                  <div className="font-mono text-[10px] text-[#94A3B8]">{a.target_id || "—"}</div>
                </td>
                <td className="px-5 py-3 text-xs text-[#475569] dark:text-slate-400 max-w-md">
                  {a.details && Object.keys(a.details).length > 0 ? (
                    <code className="text-[10px]">{JSON.stringify(a.details).slice(0, 200)}</code>
                  ) : "—"}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="px-5 py-12 text-center text-[#475569] dark:text-slate-400">
                <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                No admin actions recorded {filter ? "matching this filter" : "yet"}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
